import { useState } from "react";
import {
  useGetEmailConfigsQuery,
  useCreateEmailConfigMutation,
  useUpdateEmailConfigMutation,
  useDeleteEmailConfigMutation,
  useSetActiveEmailConfigMutation,
  useSendTestEmailMutation,
  useGetAppConfigQuery,
  useUpdateAppConfigMutation,
} from "../../redux/api";
import type { EmailConfig } from "../../redux/api";
import { Modal } from "../../components/Modal";
import { Field } from "../../components/Field";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCheck,
  IconSend,
} from "../../components/icons";
import { RowMenu } from "../../components/RowMenu";
import { validateEmail } from "../../utils/validation";

const COLUMN_COUNT = 5;

type CfgForm = {
  name: string;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  apiKey: string;
  isActive: boolean;
};

const emptyForm: CfgForm = {
  name: "",
  fromEmail: "",
  fromName: "",
  replyTo: "",
  apiKey: "",
  isActive: false,
};

// Platform Owner → Settings → Email Configuration. Manage named SendGrid
// configurations; one is active (used to actually send mail). API keys are
// write-only — only the last 4 chars are shown after saving.
const EmailConfigScreen = () => {
  const { data: configs = [], isLoading, error, refetch } =
    useGetEmailConfigsQuery();

  const [createConfig, { isLoading: creating }] =
    useCreateEmailConfigMutation();
  const [updateConfig, { isLoading: updating }] =
    useUpdateEmailConfigMutation();
  const [deleteConfig] = useDeleteEmailConfigMutation();
  const [setActive] = useSetActiveEmailConfigMutation();
  const [sendTest, { isLoading: sendingTest }] = useSendTestEmailMutation();

  const { data: appConfig } = useGetAppConfigQuery();
  const [updateAppConfig, { isLoading: togglingService }] =
    useUpdateAppConfigMutation();
  const serviceEnabled = appConfig?.emailServiceEnabled ?? true;

  const handleToggleService = async () => {
    const next = !serviceEnabled;
    if (
      !next &&
      !window.confirm(
        "Disable the email service? Password/user/product notification emails will stop sending until re-enabled. Core inventory features are unaffected.",
      )
    ) {
      return;
    }
    try {
      await updateAppConfig({ emailServiceEnabled: next }).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to update email service.",
      );
    }
  };

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CfgForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  // Per-field validation messages — shown directly under the offending input
  // (with a red border) instead of the single top-of-form banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const saving = creating || updating;
  const isEdit = !!editingId;

  const setField = <K extends keyof CfgForm>(key: K, value: CfgForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFormError(null);
    setFieldErrors({});
    setOpen(true);
  };

  const openEdit = (cfg: EmailConfig) => {
    setForm({
      name: cfg.name ?? "",
      fromEmail: cfg.fromEmail ?? "",
      fromName: cfg.fromName ?? "",
      replyTo: (cfg.replyTo ?? []).join(", "),
      apiKey: "",
      isActive: cfg.isActive,
    });
    setEditingId(cfg.id);
    setFormError(null);
    setFieldErrors({});
    setOpen(true);
  };

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) {
      errors.name = "Name is required.";
    }
    const emailErr = validateEmail(form.fromEmail);
    if (emailErr) errors.fromEmail = emailErr;

    if (!isEdit && !form.apiKey.trim()) {
      errors.apiKey = "An API key is required.";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const replyTo = form.replyTo
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    try {
      if (isEdit && editingId) {
        await updateConfig({
          id: editingId,
          input: {
            name: form.name.trim(),
            fromEmail: form.fromEmail.trim(),
            fromName: form.fromName.trim() || undefined,
            replyTo: replyTo.length ? replyTo : undefined,
            // Blank = keep the existing key.
            apiKey: form.apiKey.trim() || undefined,
            isActive: form.isActive,
          },
        }).unwrap();
      } else {
        await createConfig({
          name: form.name.trim(),
          fromEmail: form.fromEmail.trim(),
          fromName: form.fromName.trim() || undefined,
          replyTo: replyTo.length ? replyTo : undefined,
          apiKey: form.apiKey.trim(),
          isActive: form.isActive,
        }).unwrap();
      }
      setOpen(false);
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save configuration.",
      );
    }
  };

  const handleDelete = async (cfg: EmailConfig) => {
    if (!window.confirm(`Delete email configuration "${cfg.name}"?`)) return;
    setBusyId(cfg.id);
    try {
      await deleteConfig(cfg.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleSetActive = async (cfg: EmailConfig) => {
    setBusyId(cfg.id);
    try {
      await setActive(cfg.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to set active.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleSendTest = async (cfg: EmailConfig) => {
    const to = window.prompt(
      `Send a test email using "${cfg.name}" to which address?`,
    );
    if (!to) return;
    setBusyId(cfg.id);
    try {
      await sendTest({ id: cfg.id, to }).unwrap();
      window.alert(`Test email sent to ${to}.`);
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to send test email.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load configurations"
    : null;

  const canSave =
    !!form.name.trim() &&
    !!form.fromEmail.trim() &&
    (isEdit || !!form.apiKey.trim());

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Email Configuration</h2>
            <p className="panel__sub">{configs.length} configuration(s)</p>
          </div>
          <div className="toolbar">
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
            >
              Refresh
            </button>
            <span className={`badge ${serviceEnabled ? "badge--success" : "badge--danger"}`}>
              <span className="badge__dot" />
              Email service {serviceEnabled ? "enabled" : "disabled"}
            </span>
            <button
              type="button"
              className={`btn ${serviceEnabled ? "btn--danger" : ""}`}
              onClick={handleToggleService}
              disabled={togglingService}
              title="Stops all password/user/product notification emails without affecting core inventory features."
            >
              {togglingService
                ? "Saving…"
                : serviceEnabled
                  ? "Disable email service"
                  : "Enable email service"}
            </button>
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              <IconPlus /> New configuration
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>From</th>
                <th>API key</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading configurations…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : configs.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    No email configurations yet. Add one to enable outbound
                    email.
                  </td>
                </tr>
              ) : (
                configs.map((cfg: EmailConfig) => (
                  <tr key={cfg.id}>
                    <td className="cell-title">{cfg.name}</td>
                    <td className="cell-date">
                      {cfg.fromName
                        ? `${cfg.fromName} <${cfg.fromEmail}>`
                        : cfg.fromEmail}
                    </td>
                    <td>
                      {cfg.apiKeyLast4 ? (
                        <span className="pill pill--neutral">
                          ••••{cfg.apiKeyLast4}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {cfg.isActive ? (
                        <span className="badge badge--success">
                          <span className="badge__dot" />
                          Active
                        </span>
                      ) : (
                        <span className="badge badge--info">
                          <span className="badge__dot" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "Set active",
                            icon: <IconCheck size={16} />,
                            onSelect: () => handleSetActive(cfg),
                            disabled: busyId === cfg.id,
                            hidden: cfg.isActive,
                          },
                          {
                            label: "Send test",
                            icon: <IconSend size={16} />,
                            onSelect: () => handleSendTest(cfg),
                            disabled: busyId === cfg.id || sendingTest,
                          },
                          {
                            label: "Edit configuration",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openEdit(cfg),
                          },
                          {
                            label: "Delete configuration",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(cfg),
                            danger: true,
                            disabled: busyId === cfg.id,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        open={open}
        title={isEdit ? "Edit email configuration" : "New email configuration"}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSave}
              disabled={saving || !canSave}
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
            </button>
          </>
        }
      >
        {formError ? <div className="form-error">{formError}</div> : null}
        <div className="form-grid">
          <Field label="Name *" error={fieldErrors.name} full>
            <input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="e.g. Transactional (SendGrid)"
            />
          </Field>
          <Field label="From email *" error={fieldErrors.fromEmail}>
            <input
              type="email"
              value={form.fromEmail}
              onChange={(e) => setField("fromEmail", e.target.value)}
              placeholder="no-reply@yourco.com"
            />
          </Field>
          <label className="field">
            From name
            <input
              value={form.fromName}
              onChange={(e) => setField("fromName", e.target.value)}
              placeholder="Your Company"
            />
          </label>
          <label className="field">
            Reply-to (comma-separated)
            <input
              value={form.replyTo}
              onChange={(e) => setField("replyTo", e.target.value)}
              placeholder="support@yourco.com, sales@yourco.com"
            />
          </label>
          <Field
            label={`SendGrid API key ${isEdit ? "" : "*"}`}
            error={fieldErrors.apiKey}
          >
            <input
              type="password"
              value={form.apiKey}
              onChange={(e) => setField("apiKey", e.target.value)}
              autoComplete="off"
              placeholder={isEdit ? "Leave blank to keep current key" : "SG.…"}
            />
          </Field>
          <label
            className="field field--full"
            style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}
          >
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField("isActive", e.target.checked)}
              style={{ width: "auto" }}
            />
            Make this the active sender
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default EmailConfigScreen;
